import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { api, money, ApiError } from "../lib/api";
import { useMeContext } from "../lib/MeContext";
import { Card, Notice } from "../components/ui";
/*
  The prices come from the SAME constant the Worker and Stripe read.
  They used to be hardcoded here as `?? 1899` / `?? 15900` fallbacks for exactly the
  case below — a signed-out visitor, who has no /api/me to read them from. A price
  that can drift from the real one, shown only to people who have not bought yet, is
  the worst possible place for that drift to live.
*/
import { PRICING, UNLOCKS, UNLOCK_IDS, type UnlockId } from "@config/plans";
import { useUnlocks, refreshUnlocks } from "../lib/useUnlocks";

/**
 * What Pro buys TODAY.
 *
 * This list used to promise range grids, a full leak report and a spaced-repetition
 * queue. None of the three had a route, a component or an endpoint. Selling them at
 * $18.99 a month is not an aspiration, it is a refund — so the list is now the
 * shipped product, and the roadmap sits underneath it labelled as a roadmap.
 */
const PRO_FEATURES = [
  "Every published spot, heads-up through 9-max",
  "All ICM contexts — bubble, ladder, chip EV",
  "Unlimited spots, every day",
  "Every decision graded against the solve, with what it cost",
  /* PHASE 9: shipped. The leak report came off the "on the way" list below — it had
     been live since Phase 5 while this page still called it a roadmap item. */
  "The full leak report, every segment",
  /* PHASE 9, master plan §9: Poker Pro is the bundle. Cribbage Pro is included now and
     has something to open the day the room does. */
  "Blackjack Pro and Cribbage Pro, included (cribbage when its room opens)",
];

/** Named as what it is: not yet built, and not part of what you are paying for. */
const PRO_SOON = [
  /* PHASE 9: built, and Pro — but it orders the Study, and no Study lesson is written
     yet. It moves up to the list above with the first ones. */
  "Your Syllabus — the Study, in the order your leak report says",
  /*
   * The range grid came OFF this list by being built rather than by being dropped,
   * and it is not on the paid list either: it draws numbers the node route already
   * sends with every spot, so it is free for everybody and the meter on spots is what
   * Pro actually buys. Promising it as a future paid feature would now be selling
   * something the free tier already has.
   */
  "Spaced-repetition review queue",
  "Bounty (PKO) spots",
];

export function Pricing() {
  const { getToken, isSignedIn } = useAuth();
  const { me } = useMeContext();
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState<"month" | "year" | UnlockId | null>(null);
  const unlocks = useUnlocks();
  const [error, setError] = useState<string | null>(null);
  /* Which button the error belongs to, so it is shown beside it. */
  const [errorAt, setErrorAt] = useState<"plan" | "unlock">("plan");

  const trialDays = me?.pricing.trialDays ?? PRICING.trialDays;
  const monthlyCents = me?.pricing.monthly.amountCents ?? PRICING.monthly.amountCents;
  const annualCents = me?.pricing.annual.amountCents ?? PRICING.annual.amountCents;
  const savedCents = monthlyCents * 12 - annualCents;

  async function go(interval: "month" | "year") {
    setBusy(interval);
    setError(null);
    setErrorAt("plan");
    try {
      const { url } = await api.checkout(getToken, interval, coupon.trim() || undefined);
      window.location.href = url;
    } catch (e) {
      const err = e as ApiError;
      setError(
        err.code === "rate_limited"
          ? "Too many attempts just now. Wait a minute and try again."
          : err.code === "price_not_configured"
          ? "Billing isn't configured yet — that's on us, not you. Let us know and we'll fix it."
          : "Couldn't start checkout. Try again."
      );
      setBusy(null);
    }
  }

  /** PHASE 9: one unlock, paid once, through Stripe Checkout (`mode: payment`). */
  async function buy(product: UnlockId) {
    setBusy(product);
    setError(null);
    setErrorAt("unlock");
    try {
      const { url } = await api.checkoutUnlock(getToken, product);
      window.location.href = url;
    } catch (e) {
      const err = e as ApiError;
      /* Owned or included after all (bought in another tab, or a plan since): ask again,
         so the tile says so. */
      if (err.code === "already_owned" || err.code === "included_in_plan") refreshUnlocks();
      setError(
        err.code === "rate_limited" ? "Too many attempts just now. Wait a minute and try again."
        : err.code === "room_coming" ? `${UNLOCKS[product].label} goes on sale when the ${UNLOCKS[product].room} room opens.`
        : err.code === "already_owned" ? `You already own ${UNLOCKS[product].label}.`
        : err.code === "included_in_plan" ? `${UNLOCKS[product].label} is already included in your plan.`
        : err.code === "price_not_configured" ? "Billing isn't configured yet — that's on us, not you. Let us know and we'll fix it."
        : "Couldn't start checkout. Try again."
      );
      setBusy(null);
    }
  }

  /**
   * THE PAGE SHOWS ITS PRICES TO EVERYONE.
   *
   * It did not. A signed-out visitor — the exact person this page exists to convert —
   * got a card reading "Create an account first" with no price, no feature list, no
   * trial length and nothing else. A page called Pricing that shows no pricing to
   * anyone who has not already signed up has no reason to exist.
   *
   * The information is public; only CHECKOUT needs an account, because the trial
   * attaches to one. So the content is the same for everybody and only the call to
   * action changes. A customer already on Pro also sees it — "what am I paying for"
   * is a fair question from someone paying, and the alternative was a dead-end card.
   */
  const isPro = me?.plan.id === "pro";

  return (
    <Card>
      <h1>{isPro ? "You're on Poker Pro" : "Poker Pro"}</h1>
      <p className="lede">
        {isPro
          ? "Here is what that includes. Billing is changed or cancelled from your account."
          : `${trialDays} days free. Card required, cancel any time before it ends and you're not charged.`}
      </p>

      <ul className="features">
        {PRO_FEATURES.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>

      <details className="soon">
        <summary>On the way — not what you're paying for today</summary>
        <ul className="features features-soon">
          {PRO_SOON.map((f) => <li key={f}>{f}</li>)}
        </ul>
      </details>

      {/*
        The prices are shown to everyone; the button is what changes. A signed-out
        visitor gets a link to sign up rather than a checkout that would fail, and
        somebody already paying gets the numbers without a button at all.
      */}
      <div className="plans">
        <Plan cents={monthlyCents} term="per month"
              cta={isPro ? null : !isSignedIn ? "Create account" : busy === "month" ? "Opening…" : "Start trial"}
              to={!isSignedIn ? "/sign-up" : undefined}
              disabled={busy !== null} onClick={() => go("month")} />

        <Plan cents={annualCents} term="per year" best tag={`Save ${money(savedCents)}`}
              cta={isPro ? null : !isSignedIn ? "Create account" : busy === "year" ? "Opening…" : "Start trial"}
              to={!isSignedIn ? "/sign-up" : undefined}
              disabled={busy !== null} onClick={() => go("year")} />
      </div>

      {isPro && (
        <p className="row">
          <Link className="btn-quiet" to="/account">Manage billing</Link>
        </p>
      )}

      {!isSignedIn && (
        <p className="muted small">
          The trial attaches to an account, so signing up comes first.{" "}
          <Link to="/sign-in">Already have one?</Link>
        </p>
      )}

      {isSignedIn && !isPro && <label className="field field-narrow">
        <span>Coupon code (optional)</span>
        <input
          value={coupon}
          onChange={(e) => setCoupon(e.target.value.toUpperCase().slice(0, 32))}
          placeholder="FOUNDER"
          autoComplete="off"
          spellCheck={false}
        />
      </label>}

      {error && errorAt === "plan" && <Notice kind="err">{error}</Notice>}

      {/*
        * ===================== PHASE 9: THE TWO ONE-TIME UNLOCKS ======================
        * Master plan §9, signed off 11 September 2026. Poker Pro above includes both.
        * For somebody who came for one room, each is its own door, paid once. The
        * prices and the lists are `UNLOCKS` in `config/plans.ts` — the Worker's copy.
        *
        * Cribbage Pro is shown and not sold here: the room is "Coming", and a buy
        * button for a room nobody can open is the refund this page's first comment is
        * about. The Worker will sell it the day the room opens (and the store listings
        * can); this tile says so rather than taking the money early.
        * =============================================================================
        */}
      <section className="unlocks" aria-labelledby="unlocks-h">
        <h2 id="unlocks-h">Here for one room?</h2>
        <p className="muted">
          The tables are free in every room, on every plan. Blackjack&rsquo;s analysis, and
          cribbage&rsquo;s when its room opens, are also sold on their own, once — no
          subscription.
        </p>
        <div className="plans">
          {UNLOCK_IDS.map((id) => {
            const u = UNLOCKS[id];
            const held = unlocks.has(id);
            const cta = held ? null
              : u.roomComing ? null
              : !isSignedIn ? "Create account"
              : unlocks.loading ? "Checking…"
              : busy === id ? "Opening…" : "Buy once";
            return (
              <div key={id} className="unlock" data-td={`unlock-${id}`}>
                <Plan cents={u.amountCents} term={`${u.label} · once`}
                      tag={held ? (unlocks.viaPlan ? "In your plan" : "Yours") : u.roomComing ? "Room coming" : undefined}
                      cta={cta} to={!isSignedIn ? "/sign-up" : undefined}
                      disabled={busy !== null || unlocks.loading} onClick={() => void buy(id)} />
                <ul className="features features-unlock">
                  {u.grants.map((g) => <li key={g}>{g}</li>)}
                </ul>
                {u.roomComing && !held && (
                  <p className="muted small">
                    Not on sale until the {u.room} room opens. It is included in Poker Pro
                    now, so a subscriber has it the day it does.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {error && errorAt === "unlock" && <Notice kind="err">{error}</Notice>}

      <p className="muted small footnote">
        Payments are handled by Stripe. We never see your card details.
      </p>
    </Card>
  );
}

/**
 * One plan tile.
 *
 * Renders as a link, a button or neither, depending on what the viewer can actually
 * do — while the price and the term stay identical in all three. That is the whole
 * point of the component: the numbers are public and only the action is conditional,
 * and keeping them in one place stops the two from drifting apart.
 */
function Plan({ cents, term, cta, to, tag, best, disabled, onClick }: {
  cents: number; term: string;
  /** Null when the viewer has nothing to do here — they are already paying. */
  cta: string | null;
  /** Set when the action is navigation rather than checkout. */
  to?: string;
  tag?: string; best?: boolean; disabled?: boolean; onClick?: () => void;
}) {
  const inner = (
    <>
      {tag && <span className="plan-tag">{tag}</span>}
      <span className="plan-price num">{money(cents)}</span>
      <span className="plan-term">{term}</span>
      {cta && <span className="plan-cta">{cta}</span>}
    </>
  );
  const cls = `plan${best ? " plan-best" : ""}`;

  if (!cta) return <div className={`${cls} plan-static`}>{inner}</div>;
  if (to) return <Link className={cls} to={to}>{inner}</Link>;
  return <button className={cls} disabled={disabled} onClick={onClick}>{inner}</button>;
}
